import { computed, readonly, shallowRef } from 'vue';

// Light / dark / system appearance, kept per device (localStorage) like the
// Laravel starter kit. Dark mode puts `dark` on <html> (the CSS tokens in
// global.css) and switches Vuetify to its dark theme. public/index.html
// applies the saved choice before the app loads so there's no flash.
const STORAGE_KEY = 'appearance';
const MODES = ['light', 'dark', 'system'];
const DEFAULT_MODE = 'system';

export const APPEARANCE_OPTIONS = [
  { value: 'light', title: 'Light', icon: 'mdi-white-balance-sunny' },
  { value: 'dark', title: 'Dark', icon: 'mdi-weather-night' },
  { value: 'system', title: 'System', icon: 'mdi-monitor' },
];

function readMode() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(saved) ? saved : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

const media =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

const mode = shallowRef(readMode());
const systemDark = shallowRef(Boolean(media?.matches));
const isDark = computed(
  () => mode.value === 'dark' || (mode.value === 'system' && systemDark.value),
);

let vuetifyTheme = null;

function apply() {
  document.documentElement.classList.toggle('dark', isDark.value);
  if (!vuetifyTheme) return;
  const name = isDark.value ? 'dark' : 'light';
  if (typeof vuetifyTheme.change === 'function') vuetifyTheme.change(name);
  else vuetifyTheme.global.name.value = name;
}

function setMode(next) {
  if (!MODES.includes(next)) return;
  mode.value = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private mode etc.: the choice still applies for this visit.
  }
  apply();
}

// Called once from main.js with the Vuetify instance.
export function installAppearance(vuetify) {
  vuetifyTheme = vuetify.theme;
  media?.addEventListener('change', (event) => {
    systemDark.value = event.matches;
    apply();
  });
  // Another tab changed it.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    mode.value = readMode();
    apply();
  });
  apply();
}

export function useAppearance() {
  return { mode: readonly(mode), isDark, setMode };
}
